/* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
import { useCallback, useEffect, useRef, useState } from 'react';

interface FrameData {
  frameNumber: number;
  url: string;
}

interface UseFrameLoaderOptions {
  apiUrl: string;
  onFramesLoaded?: (frames: FrameData[], fromCache: boolean) => void;
  onError?: (error: string) => void;
  onImagesProcessing?: (frames: FrameData[]) => void;
}

// Singleton worker instance shared across all components
let sharedWorker: Worker | null = null;
let workerRefCount = 0;

type PendingRequest = {
  onFrames: (frames: FrameData[], fromCache: boolean) => void;
  onImagesProcessing?: (frames: FrameData[]) => void;
  onError?: (error: string) => void;
  onComplete: () => void;
};

// Request tracking. All callbacks are request-scoped: the shared worker must
// never deliver a response to whichever hook happened to create it first.
const pendingRequests = new Map<string, PendingRequest>();

// Global counter for unique hook instances
let hookInstanceCounter = 0;

export function useFrameLoader({
  apiUrl,
  onFramesLoaded,
  onError,
  onImagesProcessing,
}: UseFrameLoaderOptions) {
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);
  const hookInstanceId = useRef(++hookInstanceCounter);
  const requestIdsRef = useRef(new Set<string>());

  // Initialize worker on first use
  useEffect(() => {
    // No Worker outside a browser (jsdom under test, SSR). `loadFrames`
    // already no-ops without one, so the caller simply gets no frames rather
    // than an exception thrown out of an effect.
    if (typeof Worker === 'undefined') return;

    if (!sharedWorker) {
      sharedWorker = new Worker('/frameLoader.worker.js');

      // Handle messages from worker
      sharedWorker.addEventListener('message', event => {
        const { type, id, frames, error, fromCache } = event.data;

        switch (type) {
          case 'frames': {
            pendingRequests.get(id)?.onFrames(frames, !!fromCache);
            break;
          }

          case 'images-processing': {
            pendingRequests.get(id)?.onImagesProcessing?.(frames);
            break;
          }

          case 'error': {
            const request = pendingRequests.get(id);
            request?.onError?.(error || 'Failed to load frames');
            request?.onComplete();
            pendingRequests.delete(id);
            break;
          }

          case 'complete': {
            const request = pendingRequests.get(id);
            request?.onComplete();
            pendingRequests.delete(id);
            break;
          }
        }
      });
    }

    workerRefCount++;

    return () => {
      requestIdsRef.current.forEach(id => pendingRequests.delete(id));
      requestIdsRef.current.clear();
      workerRefCount--;
      // Don't terminate worker immediately - keep it alive for other components
      if (workerRefCount === 0) {
        setTimeout(() => {
          if (workerRefCount === 0 && sharedWorker) {
            sharedWorker.terminate();
            sharedWorker = null;
          }
        }, 5000); // Keep worker alive for 5 seconds after last component unmounts
      }
    };
  }, []);

  // Load frames function
  const loadFrames = useCallback(
    (
      storyId: string,
      videoId: string,
      frameNumbers: number[],
      onLoadError?: (error: string) => void,
      onRequestComplete?: () => void,
    ) => {
      if (!sharedWorker || frameNumbers.length === 0) return;

      const requestId = `${storyId}:${videoId}:${
        hookInstanceId.current
      }:${++requestIdRef.current}`;
      setIsLoading(true);
      requestIdsRef.current.add(requestId);

      pendingRequests.set(requestId, {
        onFrames: (frames, fromCache) => {
          onFramesLoaded?.(frames, fromCache);
        },
        onImagesProcessing,
        onError: error => {
          onLoadError?.(error);
          onError?.(error);
        },
        onComplete: () => {
          requestIdsRef.current.delete(requestId);
          setIsLoading(requestIdsRef.current.size > 0);
          onRequestComplete?.();
        },
      });

      // Send request to worker
      sharedWorker.postMessage({
        type: 'loadFrames',
        id: requestId,
        apiUrl,
        storyId,
        videoId,
        frameNumbers,
      });
    },
    [apiUrl, onError, onFramesLoaded, onImagesProcessing],
  );

  // Clear cache for a specific video
  const clearCache = useCallback((storyId?: string, videoId?: string) => {
    if (!sharedWorker) return;

    sharedWorker.postMessage({
      type: 'clearCache',
      storyId,
      videoId,
    });
  }, []);

  return {
    loadFrames,
    clearCache,
    isLoading,
  };
}
